import React, { useCallback, useEffect, useRef } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import Placeholder from "@tiptap/extension-placeholder";
import {
  Bold,
  Italic,
  List,
  ListOrdered,
  Heading2,
  Heading3,
  Link2,
  Link2Off,
  Image as ImageIcon,
  Quote,
  Undo2,
  Redo2,
  Loader2,
} from "lucide-react";
import { uploadInlineNewsImage } from "@/api/news";

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

// Normaliseer /api/uploads/... naar absolute URL zodat plaatjes ook in de editor
// preview zichtbaar zijn (backend en frontend delen dezelfde host in productie).
const absUrl = (u) => {
  if (!u) return u;
  if (/^https?:\/\//i.test(u)) return u;
  if (u.startsWith("/")) return `${BACKEND_URL}${u}`;
  return u;
};

const ToolbarButton = ({ onClick, active, disabled, title, testId, children }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    data-testid={testId}
    className={`p-2 rounded-md text-sm font-[Poppins] transition ${
      active
        ? "bg-[#173A5E] text-white"
        : "text-[#173A5E] hover:bg-[#F4F8FB]"
    } disabled:opacity-40 disabled:cursor-not-allowed`}
  >
    {children}
  </button>
);

export const RichTextEditor = ({ value, onChange, onError }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = React.useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3, 4] },
      }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: {
          rel: "noopener noreferrer",
          target: "_blank",
          class: "text-[#2E9CD6] underline",
        },
      }),
      Image.configure({
        HTMLAttributes: { class: "rounded-lg my-3 max-w-full" },
      }),
      Placeholder.configure({
        placeholder: "Schrijf het bericht… gebruik de knoppen hierboven voor opmaak.",
      }),
    ],
    content: value || "",
    // Belangrijk: ProseMirror-instance meteen focussen zodat de eerste
    // toetsaanslagen niet verloren gaan terwijl de editor nog "warm draait".
    autofocus: "end",
    onUpdate: ({ editor }) => {
      onChange?.(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class:
          "ll-prose min-h-[280px] p-4 focus:outline-none font-[Inter] text-[#173A5E]",
        "data-testid": "news-editor-content",
      },
    },
  });

  // Extra veiligheid: forceer focus zodra de editor klaar is, zelfs als React
  // de modal net renderde (voorkomt dat de eerste ~12 tekens gedropt worden).
  useEffect(() => {
    if (!editor) return;
    const t = setTimeout(() => {
      if (!editor.isDestroyed && !editor.isFocused) editor.commands.focus("end");
    }, 80);
    return () => clearTimeout(t);
  }, [editor]);

  // Sync when parent replaces value (e.g. loading existing post).
  useEffect(() => {
    if (!editor) return;
    if (value !== undefined && value !== editor.getHTML()) {
      editor.commands.setContent(value || "", false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, value]);

  const addLink = useCallback(() => {
    if (!editor) return;
    const previous = editor.getAttributes("link").href || "";
    // eslint-disable-next-line no-alert
    const url = window.prompt("Link-URL (laat leeg om te verwijderen)", previous);
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  }, [editor]);

  const onPickImage = () => fileRef.current?.click();

  const onImageChosen = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !editor) return;
    setUploading(true);
    try {
      const res = await uploadInlineNewsImage(file);
      editor.chain().focus().setImage({ src: absUrl(res.url) }).run();
    } catch (err) {
      onError?.(err?.response?.data?.detail || "Upload van afbeelding mislukt");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  if (!editor) return null;

  return (
    <div className="border border-[#E4EEF5] rounded-lg overflow-hidden bg-white" data-testid="news-editor">
      <div className="flex flex-wrap items-center gap-0.5 border-b border-[#E4EEF5] bg-[#F9FBFD] px-2 py-1.5">
        <ToolbarButton
          testId="editor-h2"
          title="Kop 2"
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          active={editor.isActive("heading", { level: 2 })}
        >
          <Heading2 className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton
          testId="editor-h3"
          title="Kop 3"
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          active={editor.isActive("heading", { level: 3 })}
        >
          <Heading3 className="w-4 h-4" />
        </ToolbarButton>
        <div className="w-px h-5 bg-[#E4EEF5] mx-1" />
        <ToolbarButton
          testId="editor-bold"
          title="Vet"
          onClick={() => editor.chain().focus().toggleBold().run()}
          active={editor.isActive("bold")}
        >
          <Bold className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton
          testId="editor-italic"
          title="Cursief"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          active={editor.isActive("italic")}
        >
          <Italic className="w-4 h-4" />
        </ToolbarButton>
        <div className="w-px h-5 bg-[#E4EEF5] mx-1" />
        <ToolbarButton
          testId="editor-ul"
          title="Opsomming"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          active={editor.isActive("bulletList")}
        >
          <List className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton
          testId="editor-ol"
          title="Genummerde lijst"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          active={editor.isActive("orderedList")}
        >
          <ListOrdered className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton
          testId="editor-quote"
          title="Citaat"
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          active={editor.isActive("blockquote")}
        >
          <Quote className="w-4 h-4" />
        </ToolbarButton>
        <div className="w-px h-5 bg-[#E4EEF5] mx-1" />
        <ToolbarButton
          testId="editor-link"
          title="Link toevoegen/wijzigen"
          onClick={addLink}
          active={editor.isActive("link")}
        >
          <Link2 className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton
          testId="editor-unlink"
          title="Link verwijderen"
          onClick={() => editor.chain().focus().unsetLink().run()}
          disabled={!editor.isActive("link")}
        >
          <Link2Off className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton
          testId="editor-image"
          title="Afbeelding invoegen"
          onClick={onPickImage}
          disabled={uploading}
        >
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4" />}
        </ToolbarButton>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          onChange={onImageChosen}
          className="hidden"
          data-testid="editor-image-input"
        />
        <div className="ml-auto flex items-center gap-0.5">
          <ToolbarButton
            testId="editor-undo"
            title="Ongedaan maken"
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!editor.can().undo()}
          >
            <Undo2 className="w-4 h-4" />
          </ToolbarButton>
          <ToolbarButton
            testId="editor-redo"
            title="Opnieuw"
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!editor.can().redo()}
          >
            <Redo2 className="w-4 h-4" />
          </ToolbarButton>
        </div>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
};

export default RichTextEditor;
